import { useMemo } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import Fuse, { type IFuseOptions } from "fuse.js";
import { collections } from "@/client/db";
import { useStories } from "./use-stories";
import { useCodex, type CodexType } from "./use-codex";
import { useChapters } from "./use-chapters";
import { useChats } from "./use-chats";

// Unified search result type
export interface SearchResult {
  id: string;
  type: "story" | "codex" | "part" | "chapter" | "scene" | "idea" | "chat";
  title: string;
  subtitle?: string;
  storyId?: string;
  chapterId?: string; // For scenes
  codexType?: CodexType;
}

// Internal type for Fuse.js indexing
interface SearchableItem extends SearchResult {
  // Additional searchable fields
  description?: string | null;
  content?: string | null;
  tags?: string[] | null;
  summary?: string | null;
  notes?: string | null;
}

const FUSE_OPTIONS: IFuseOptions<SearchableItem> = {
  keys: [
    { name: "title", weight: 2 },
    { name: "description", weight: 1 },
    { name: "content", weight: 0.8 },
    { name: "tags", weight: 1 },
    { name: "summary", weight: 1 },
    { name: "notes", weight: 0.6 },
    { name: "subtitle", weight: 0.5 },
  ],
  threshold: 0.4,
  includeScore: true,
  ignoreLocation: true,
  minMatchCharLength: 2,
};

const CODEX_TYPE_LABELS: Record<CodexType, string> = {
  character: "Character",
  location: "Location",
  object: "Object",
  event: "Event",
  lore: "Lore",
};

// Strip HTML tags from content for better search matching
function stripHtml(html: string | null | undefined): string | undefined {
  if (!html) return undefined;
  // Use a simple regex to strip tags - works for basic HTML
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * Hook for global fuzzy search
 * @param storyId - If provided, searches within that story's content (codex, chapters, scenes, chats)
 *                  If not provided, searches across all stories
 */
export function useSearch(storyId?: string) {
  // Fetch all potentially searchable data
  const { data: stories = [] } = useStories();
  const { data: codexEntries = [] } = useCodex(storyId ?? "");
  const { data: chaptersWithScenes = [] } = useChapters(storyId ?? "");
  const { data: chats = [] } = useChats(storyId ?? "");
  const { data: allParts } = useLiveQuery(() => collections().parts.collection, [storyId]);
  const { data: allIdeas } = useLiveQuery(() => collections().fragments.collection, [storyId]);

  // Build the searchable items array
  const searchableItems = useMemo<SearchableItem[]>(() => {
    const items: SearchableItem[] = [];

    if (!storyId) {
      // On home page: search stories
      for (const story of stories) {
        items.push({
          id: story.id,
          type: "story",
          title: story.name,
          subtitle: story.synopsis ?? story.description ?? undefined,
          description: story.synopsis ?? story.description,
        });
      }
    } else {
      // Inside a story: search codex, chapters, scenes, chats

      // Codex entries
      for (const entry of codexEntries) {
        items.push({
          id: entry.id,
          type: "codex",
          title: entry.name,
          subtitle: CODEX_TYPE_LABELS[entry.type],
          storyId: entry.storyId,
          codexType: entry.type,
          content: stripHtml(entry.content),
          tags: entry.tags,
        });
      }

      // Parts
      for (const part of allParts ?? []) {
        if (part.storyId !== storyId) continue;
        items.push({
          id: part.id,
          type: "part",
          title: part.title,
          subtitle: "Part",
          storyId: part.storyId,
          description: part.description,
        });
      }

      // Chapters and their scenes
      for (const chapter of chaptersWithScenes) {
        items.push({
          id: chapter.id,
          type: "chapter",
          title: chapter.title,
          subtitle: `Chapter`,
          storyId: chapter.storyId,
          notes: chapter.notes,
        });

        // Scenes within this chapter
        for (const scene of chapter.scenes) {
          items.push({
            id: scene.id,
            type: "scene",
            title: scene.title,
            subtitle: `Scene in ${chapter.title}`,
            storyId: chapter.storyId,
            chapterId: scene.chapterId,
            summary: scene.summary,
            notes: scene.notes,
          });
        }
      }

      // Ideas still in play, found by their words
      for (const idea of allIdeas ?? []) {
        if (idea.storyId !== storyId || idea.archivedAt !== null) continue;
        const firstLine = idea.content.split("\n")[0] ?? "";
        items.push({
          id: idea.id,
          type: "idea",
          title: firstLine.length > 80 ? `${firstLine.slice(0, 80)}…` : firstLine,
          subtitle: "Idea",
          storyId: idea.storyId,
          content: idea.content,
        });
      }

      // Chats
      for (const chat of chats) {
        items.push({
          id: chat.id,
          type: "chat",
          title: chat.title,
          subtitle: "AI Chat",
          storyId: chat.storyId,
        });
      }
    }

    return items;
  }, [storyId, stories, codexEntries, allParts, chaptersWithScenes, allIdeas, chats]);

  // Create the Fuse instance
  const fuse = useMemo(() => {
    return new Fuse(searchableItems, FUSE_OPTIONS);
  }, [searchableItems]);

  // Search function
  const search = useMemo(() => {
    return (query: string): SearchResult[] => {
      if (!query.trim()) {
        // Return all items when query is empty (limited for performance)
        return searchableItems.slice(0, 50).map((item) => ({
          id: item.id,
          type: item.type,
          title: item.title,
          subtitle: item.subtitle,
          storyId: item.storyId,
          chapterId: item.chapterId,
          codexType: item.codexType,
        }));
      }

      const results = fuse.search(query, { limit: 20 });
      return results.map((result) => ({
        id: result.item.id,
        type: result.item.type,
        title: result.item.title,
        subtitle: result.item.subtitle,
        storyId: result.item.storyId,
        chapterId: result.item.chapterId,
        codexType: result.item.codexType,
      }));
    };
  }, [fuse, searchableItems]);

  return {
    search,
    itemCount: searchableItems.length,
  };
}
